import tinycolor from 'tinycolor2'

export type MultiStrikeColorPaletteId = 'default' | 'custom'

export type MultiStrikeThemeMode = 'light' | 'dark'

export interface MultiStrikeColorPalette {
    id: MultiStrikeColorPaletteId
    label: string
    callColors: string[]
    putColors: string[]
    callCumulative: string
    putCumulative: string
}

export type MultiStrikePaletteConfig = {
    colorPaletteId?: MultiStrikeColorPaletteId | 'vivid' | 'soft'
    customCallBaseColor?: string
    customPutBaseColor?: string
    customCallHueShift?: number
    customPutHueShift?: number
}

export const MULTI_STRIKE_PALETTE_SIZE = 10
export const DEFAULT_MULTI_STRIKE_COLOR_PALETTE_ID: MultiStrikeColorPaletteId = 'default'
export const DEFAULT_CUSTOM_CALL_BASE_COLOR = '#2F2FCD'
export const DEFAULT_CUSTOM_PUT_BASE_COLOR = '#FFA719'
export const DEFAULT_CUSTOM_CALL_HUE_SHIFT = 50
export const DEFAULT_CUSTOM_PUT_HUE_SHIFT = 50
export const MIN_CUSTOM_HUE_SHIFT = 0
export const MAX_CUSTOM_HUE_SHIFT = 360

const DEFAULT_LIGHT_PALETTE_COLORS = {
    callColors: ['#6B3FFE', '#1E90FF', '#0000CD', '#00BFFF', '#4988C6', '#16B4B4', '#00FFFF', '#32CD32', '#50C878', '#40E0D0'],
    putColors: ['#FFA719', '#FFA047', '#D8D000', '#FF6347', '#FF4500', '#DC143C', '#FF5722', '#FF0000', '#FF1493', '#FF69B4'],
    callCumulative: '#2F2FCD',
    putCumulative: '#FFA719',
}

const DEFAULT_DARK_PALETTE_COLORS = {
    callColors: ['#A5B4FC', '#93C5FD', '#7DD3FC', '#67E8F9', '#6EE7B7', '#86EFAC', '#BEF264', '#5EEAD4', '#C4B5FD', '#99F6E4'],
    putColors: ['#FDBA74', '#FCD34D', '#FDE68A', '#FCA5A5', '#F87171', '#FDA4AF', '#F9A8D4', '#E879F9', '#FB7185', '#F472B6'],
    callCumulative: '#818CF8',
    putCumulative: '#FB923C',
}

export const normalizeMultiStrikeColorPaletteId = (
    paletteId?: MultiStrikePaletteConfig['colorPaletteId']
): MultiStrikeColorPaletteId => {
    return paletteId === 'custom' ? 'custom' : 'default'
}

export const getDefaultMultiStrikeColorPalette = (themeMode: MultiStrikeThemeMode = 'light'): MultiStrikeColorPalette => {
    const colors = themeMode === 'dark' ? DEFAULT_DARK_PALETTE_COLORS : DEFAULT_LIGHT_PALETTE_COLORS
    return {
        id: 'default',
        label: 'Default',
        ...colors,
    }
}

export const getCumulativeColorFromBase = (baseColor: string) => {
    const base = tinycolor(baseColor)
    if (!base.isValid()) {
        return baseColor
    }
    return base.clone().darken(12).saturate(8).toHexString()
}

export const generatePaletteFromBaseColor = (
    baseColor: string,
    count = MULTI_STRIKE_PALETTE_SIZE,
    hueShiftRange = DEFAULT_CUSTOM_CALL_HUE_SHIFT
): string[] => {
    const base = tinycolor(baseColor)
    if (!base.isValid()) {
        return Array.from({ length: count }, () => baseColor)
    }

    const { h, s, l } = base.toHsl()

    return Array.from({ length: count }, (_, index) => {
        const progress = index / Math.max(count - 1, 1)
        const hueOffset = (progress - 0.5) * hueShiftRange
        const lightness = Math.min(0.82, Math.max(0.28, l + (progress - 0.5) * 0.42))
        const saturation = Math.min(1, Math.max(0.35, s + (index % 2 === 0 ? 0.06 : -0.04)))

        return tinycolor({
            h: (h + hueOffset + 360) % 360,
            s: saturation,
            l: lightness,
        }).toHexString()
    })
}

export const buildCustomMultiStrikeColorPalette = (
    config?: Pick<
        MultiStrikePaletteConfig,
        'customCallBaseColor' | 'customPutBaseColor' | 'customCallHueShift' | 'customPutHueShift'
    >
): MultiStrikeColorPalette => {
    const callBase = config?.customCallBaseColor ?? DEFAULT_CUSTOM_CALL_BASE_COLOR
    const putBase = config?.customPutBaseColor ?? DEFAULT_CUSTOM_PUT_BASE_COLOR
    const callHueShift = config?.customCallHueShift ?? DEFAULT_CUSTOM_CALL_HUE_SHIFT
    const putHueShift = config?.customPutHueShift ?? DEFAULT_CUSTOM_PUT_HUE_SHIFT

    return {
        id: 'custom',
        label: 'Custom',
        callColors: generatePaletteFromBaseColor(callBase, MULTI_STRIKE_PALETTE_SIZE, callHueShift),
        putColors: generatePaletteFromBaseColor(putBase, MULTI_STRIKE_PALETTE_SIZE, putHueShift),
        callCumulative: getCumulativeColorFromBase(callBase),
        putCumulative: getCumulativeColorFromBase(putBase),
    }
}

export const resolveMultiStrikeColorPalette = (
    config?: MultiStrikePaletteConfig,
    themeMode: MultiStrikeThemeMode = 'light'
): MultiStrikeColorPalette => {
    if (normalizeMultiStrikeColorPaletteId(config?.colorPaletteId) === 'custom') {
        return buildCustomMultiStrikeColorPalette(config)
    }
    return getDefaultMultiStrikeColorPalette(themeMode)
}

export const getMultiStrikeCallColor = (
    config: MultiStrikePaletteConfig | undefined,
    index: number,
    themeMode: MultiStrikeThemeMode = 'light'
) => {
    const palette = resolveMultiStrikeColorPalette(config, themeMode)
    return palette.callColors[index % palette.callColors.length]
}

export const getMultiStrikePutColor = (
    config: MultiStrikePaletteConfig | undefined,
    index: number,
    themeMode: MultiStrikeThemeMode = 'light'
) => {
    const palette = resolveMultiStrikeColorPalette(config, themeMode)
    return palette.putColors[index % palette.putColors.length]
}

export const toMultiStrikePaletteConfig = (styles: MultiStrikePaletteConfig): MultiStrikePaletteConfig => ({
    colorPaletteId: normalizeMultiStrikeColorPaletteId(styles.colorPaletteId),
    customCallBaseColor: styles.customCallBaseColor ?? DEFAULT_CUSTOM_CALL_BASE_COLOR,
    customPutBaseColor: styles.customPutBaseColor ?? DEFAULT_CUSTOM_PUT_BASE_COLOR,
    customCallHueShift: styles.customCallHueShift ?? DEFAULT_CUSTOM_CALL_HUE_SHIFT,
    customPutHueShift: styles.customPutHueShift ?? DEFAULT_CUSTOM_PUT_HUE_SHIFT,
})
